import { MessageService, MessageError } from '@/services/message.service';

const state = {
  loading: false,
  sending: false,
  saving: false,
  scheduling: false,
  outbox: [],
  drafts: [],
  schedules: [],
  outboxCurrentPage: 1,
  draftsCurrentPage: 1,
  schedulesCurrentPage: 1,
  outboxTotal: 0,
  outboxPages: 0,
  draftsTotal: 0,
  draftsPages: 0,
  schedulesTotal: 0,
  schedulesPages: 0,
  serverResponse: null,
  senderNames: [{}],
};

const getters = {
  loading: (state) => {
    return state.loading;
  },
  sending: (state) => {
    return state.sending;
  },
  saving: (state) => {
    return state.saving;
  },
  scheduling: (state) => {
    return state.scheduling;
  },

  outbox: (state) => {
    return state.outbox;
  },
  senderNames: (state) => {
    return state.senderNames;
  },
  outboxCurrentPage: (state) => {
    return state.outboxCurrentPage;
  },
  outboxTotal: (state) => {
    return state.outboxTotal;
  },
  outboxPages: (state) => {
    return state.outboxPages;
  },

  drafts: (state) => {
    return state.drafts;
  },
  draftsCurrentPage: (state) => {
    return state.draftsCurrentPage;
  },
  draftsTotal: (state) => {
    return state.draftsTotal;
  },
  draftsPages: (state) => {
    return state.draftsPages;
  },

  schedules: (state) => {
    return state.schedules;
  },
  schedulesCurrentPage: (state) => {
    return state.schedulesCurrentPage;
  },
  schedulesTotal: (state) => {
    return state.schedulesTotal;
  },
  schedulesPages: (state) => {
    return state.schedulesPages;
  },
  serverResponse: (state) => {
    return state.serverResponse;
  },
};

const actions = {
  async send({ commit }, { data }) {
    commit('sendRequest');
    try {
      const response = await MessageService.send(data);
      if (response.status === 'success') {
        let sent = await MessageService.fetchSent(response.id);
        if (sent.id > 0) {
          commit('sendSuccess', sent);
        }

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async saveAsDraft({ commit }, { data }) {
    commit('saveRequest');

    try {
      const response = await MessageService.saveAsDraft(data);
      if (response.status === 'success') {
        let draft = await MessageService.fetchDraft(response.id);
        if (draft.id > 0) {
          commit('saveAsDraftSuccess', draft);
        }

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async schedule({ commit }, { data }) {
    commit('scheduleRequest');

    try {
      const response = await MessageService.schedule(data);
      if (response.status === 'success') {
        let schedule = await MessageService.fetchSchedule(response.id);
        if (schedule.id > 0) {
          commit('scheduleSuccess', schedule);
        }

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });

        commit('scheduleFailed');
      }

      return false;
    }
  },

  async outboxFetchAll({ commit }, { page }) {
    commit('startRequest');

    try {
      const response = await MessageService.outboxFetchAll(page);
      if (response.hasOwnProperty('outbox')) {
        commit('outboxFetchAllSuccess', response);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async draftFetchAll({ commit }, { page }) {
    commit('startRequest');

    try {
      const response = await MessageService.draftFetchAll(page);
      if (response.hasOwnProperty('drafts')) {
        commit('draftFetchAllSuccess', response);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async scheduleFetchAll({ commit }, { page }) {
    commit('startRequest');

    try {
      const response = await MessageService.scheduleFetchAll(page);
      if (response.hasOwnProperty('schedules')) {
        commit('scheduleFetchAllSuccess', response);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteSent({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteSent(id);
      if (response.status === 'success') {
        commit('deleteSentSuccess', [id]);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteMultipleSent({ commit }, { ids }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteMultipleSent(ids);
      if (response.status === 'success') {
        commit('deleteSentSuccess', ids);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteDraft({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteDraft(id);
      if (response.status === 'success') {
        commit('deleteDraftSuccess', [id]);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteMultipleDrafts({ commit }, { ids }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteMultipleDrafts(ids);
      if (response.status === 'success') {
        commit('deleteDraftSuccess', ids);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteSchedule({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteSchedule(id);
      if (response.status === 'success') {
        commit('deleteScheduleSuccess', [id]);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async deleteMultipleSchedules({ commit }, { ids }) {
    commit('startRequest');

    try {
      const response = await MessageService.deleteMultipleSchedules(ids);
      if (response.status === 'success') {
        commit('deleteScheduleSuccess', ids);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async fetchSentExtra({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.fetchSentExtra(id);
      if (response.total > 0) {
        commit('fetchSentExtraSuccess', response);

        return response.recipients;
      } else {
        commit('requestError', response);

        return [];
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return [];
    }
  },

  async resend({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.resend(id);

      if (response.status === 'success') {
        let sent = await MessageService.fetchSent(response.id);
        if (sent.id > 0) {
          commit('sendSuccess', sent);
        }

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async sendDraft({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.sendDraft(id);
      if (response.status === 'success') {
        let sent = await MessageService.fetchSent(response.id);
        if (sent.id > 0) {
          commit('sendSuccess', sent);
          commit('deleteDraftSuccess', [id]);
        }

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async toggleSchedule({ commit }, { id }) {
    commit('startRequest');

    try {
      const response = await MessageService.toggleSchedule(id);
      if (response.status === 'success') {
        commit('toggleScheduleSuccess', id);

        return true;
      } else {
        commit('requestError', response);

        return false;
      }
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', { status: e.errorCode, message: e.message });
      }

      return false;
    }
  },

  async fetchSenderNames({ commit }) {
    commit('startRequest');
    try {
      const response = await MessageService.fetchSenderNames();
      commit('fetchSenderNamesSuccess', response);
      return true;
    } catch (e) {
      if (e instanceof MessageError) {
        commit('requestError', {
          status: e.errorCode,
          message: e.message,
        });
      }

      return false;
    }
  },
};

const mutations = {
  startRequest(state) {
    state.loading = true;
    state.serverResponse = null;
  },
  sendRequest(state) {
    state.sending = true;
  },
  saveRequest(state) {
    state.saving = true;
  },
  scheduleRequest(state) {
    state.scheduling = true;
  },

  sendSuccess(state, sent) {
    sent.marked = false;
    state.outbox.unshift(sent);
    state.outboxTotal++;
    state.sending = false;
  },
  saveAsDraftSuccess(state, draft) {
    draft.marked = false;
    state.drafts.unshift(draft);
    state.draftsTotal++;
    state.saving = false;
  },
  scheduleSuccess(state, schedule) {
    schedule.marked = false;
    state.schedules.unshift(schedule);
    state.schedulesTotal++;
    state.scheduling = false;
  },

  scheduleFailed(state) {
    //state.schedules.unshift(response.outbox)
    state.scheduling = false;
  },

  outboxFetchAllSuccess(state, response) {
    for (let i = 0; i < response.outbox.length; i++) {
      let flag = false;
      for (let j = 0; j < state.outbox.length; j++) {
        if (state.outbox[j].id === response.outbox[i].id) {
          flag = true;
          break;
        }
      }
      if (!flag) {
        response.outbox[i].marked = false;
        state.outbox.push(response.outbox[i]);
      }
    }
    state.outboxTotal = response.total;
    state.outboxPages = response.pages;
    state.loading = false;
  },

  draftFetchAllSuccess(state, response) {
    for (let i = 0; i < response.drafts.length; i++) {
      let flag = false;
      for (let j = 0; j < state.drafts.length; j++) {
        if (state.drafts[j].id === response.drafts[i].id) {
          flag = true;
          break;
        }
      }
      if (!flag) {
        response.drafts[i].marked = false;
        state.drafts.push(response.drafts[i]);
      }
    }
    state.draftsTotal = response.total;
    state.draftsPages = response.pages;
    state.loading = false;
  },

  scheduleFetchAllSuccess(state, response) {
    for (let i = 0; i < response.schedules.length; i++) {
      let flag = false;
      for (let j = 0; j < state.schedules.length; j++) {
        if (state.schedules[j].id === response.schedules[i].id) {
          flag = true;
          break;
        }
      }
      if (!flag) {
        response.schedules[i].marked = false;
        state.schedules.push(response.schedules[i]);
      }
    }
    state.schedulesTotal = response.total;
    state.schedulesPages = response.pages;
    state.loading = false;
  },

  requestError(state, response) {
    state.loading = false;
    state.serverResponse = response;
  },

  deleteSentSuccess(state, ids) {
    for (let h = 0; h < ids.length; h++) {
      for (let i = 0; i < state.outbox.length; i++) {
        if (state.outbox[i].id === ids[h]) {
          state.outbox.splice(i, 1);
          state.outboxTotal--;
          i--;
          break;
        }
      }
    }

    state.loading = false;
  },

  deleteScheduleSuccess(state, ids) {
    for (let h = 0; h < ids.length; h++) {
      for (let i = 0; i < state.schedules.length; i++) {
        if (state.schedules[i].id === ids[h]) {
          state.schedules.splice(i, 1);
          state.schedulesTotal--;
          i--;
          break;
        }
      }
    }

    state.loading = false;
  },

  deleteDraftSuccess(state, ids) {
    for (let h = 0; h < ids.length; h++) {
      for (let i = 0; i < state.drafts.length; i++) {
        if (state.drafts[i].id === ids[h]) {
          state.drafts.splice(i, 1);
          state.draftsTotal--;
          i--;
          break;
        }
      }
    }

    state.loading = false;
  },

  fetchSentExtraSuccess(state, response) {
    for (let i = 0; i < state.outbox.length; i++) {
      if (state.outbox[i].id === response.outbox_id) {
        state.outbox[i].extra = response.recipients;
        break;
      }
    }
    state.loading = false;
  },

  toggleScheduleSuccess(state, id) {
    for (let i = 0; i < state.schedules.length; i++) {
      if (state.schedules[i].id === id) {
        state.schedules[i].stopped = !state.schedules[i].stopped;
        break;
      }
    }
    state.loading = false;
  },

  fetchSenderNamesSuccess(state, response) {
    state.senderNames = response;
    state.loading = false;
  },
};

export const message = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
